import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useAuth } from './AuthContext'

const CategoriesContext = createContext(null)

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

export const CategoriesProvider = ({ children }) => {
  const { isAuthenticated } = useAuth()
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchCategories = useCallback(async (includeArchived = false) => {
    if (!isAuthenticated) {
      setCategories([])
      setLoading(false)
      return
    }
    
    try {
      const response = await axios.get(`${API}/categories`, {
        params: { include_archived: includeArchived }
      })
      setCategories(response.data)
    } catch (error) {
      console.error('Error fetching categories:', error)
      // Try to initialize default categories
      try {
        await axios.post(`${API}/categories/init`)
        const response = await axios.get(`${API}/categories`)
        setCategories(response.data)
      } catch (initError) {
        console.error('Error initializing categories:', initError)
      }
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (isAuthenticated) {
      fetchCategories()
    } else {
      setCategories([])
      setLoading(false)
    }
  }, [isAuthenticated, fetchCategories])

  const addCategory = async (categoryData) => {
    const response = await axios.post(`${API}/categories`, categoryData)
    setCategories(prev => [...prev, response.data].sort((a, b) => a.sort_order - b.sort_order))
    return response.data
  }

  const updateCategory = async (categoryId, categoryData) => {
    const response = await axios.put(`${API}/categories/${categoryId}`, categoryData)
    setCategories(prev => 
      prev.map(cat => cat.id === categoryId ? response.data : cat)
        .sort((a, b) => a.sort_order - b.sort_order)
    )
    return response.data
  }

  const reorderCategories = async (categoryIds) => {
    const response = await axios.put(`${API}/categories/reorder`, { category_ids: categoryIds })
    setCategories(response.data)
    return response.data
  }

  const deleteCategory = async (categoryId, action = 'archive', reassignTo = null) => {
    const params = new URLSearchParams({ action })
    if (reassignTo) params.append('reassign_to', reassignTo)
    
    const response = await axios.delete(`${API}/categories/${categoryId}?${params.toString()}`)
    
    if (action === 'archive') {
      setCategories(prev => 
        prev.map(cat => cat.id === categoryId ? { ...cat, is_active: false } : cat)
      )
    } else {
      setCategories(prev => prev.filter(cat => cat.id !== categoryId))
    }
    
    return response.data
  }

  const restoreCategory = async (categoryId) => {
    const response = await axios.put(`${API}/categories/${categoryId}`, { is_active: true })
    setCategories(prev => 
      prev.map(cat => cat.id === categoryId ? response.data : cat)
    )
    return response.data
  }

  const getCategoryById = (id) => {
    return categories.find(cat => cat.id === id) || null
  }

  const activeCategories = categories.filter(cat => cat.is_active)
  const archivedCategories = categories.filter(cat => !cat.is_active)

  return (
    <CategoriesContext.Provider value={{
      categories,
      activeCategories,
      archivedCategories,
      loading,
      fetchCategories,
      addCategory,
      updateCategory,
      reorderCategories,
      deleteCategory,
      restoreCategory,
      getCategoryById
    }}>
      {children}
    </CategoriesContext.Provider>
  )
}

export const useCategories = () => {
  const context = useContext(CategoriesContext)
  if (!context) {
    throw new Error('useCategories must be used within a CategoriesProvider')
  }
  return context
}
